import { useLanguage } from "@/contexts/LanguageContext";
import { SEO } from "@/components/SEO";
import { RefreshCw, Clock, CreditCard, AlertTriangle } from "lucide-react";

import { PageHero } from "@/components/shared/PageHero";
import { GlowCard } from "@/components/shared/GlowCard";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";

const ReturnPolicy = () => {
  const { language } = useLanguage();
  const { ref } = useRevealOnScroll<HTMLDivElement>();

  const sections =
    language === "ar"
      ? [
          { title: "سياسة الإلغاء والاسترداد", content: "يمكن إلغاء الحملة الإعلانية قبل 48 ساعة من تاريخ البدء. سيتم استرداد المبلغ المدفوع بالكامل خلال 7-14 يوم عمل.", icon: RefreshCw, accent: "blue" as const },
          { title: "شروط الإلغاء", content: "لا يمكن إلغاء الحملة الإعلانية بعد بدء العرض. في حالة وجود مشاكل تقنية، سيتم تعويض العميل بأيام إضافية.", icon: Clock, accent: "violet" as const },
          { title: "عملية الاسترداد", content: "يتم استرداد المبلغ إلى نفس وسيلة الدفع التي تم استخدامها في عملية الشراء الأصلية.", icon: CreditCard, accent: "emerald" as const },
          { title: "الظروف الاستثنائية", content: "في حالات القوة القاهرة أو الظروف الخارجة عن إرادتنا، نحتفظ بالحق في تعديل أو تعليق الحملة مع تقديم تعويض مناسب.", icon: AlertTriangle, accent: "amber" as const },
        ]
      : [
          { title: "Cancellation and Refund Policy", content: "Advertising campaigns can be cancelled up to 48 hours before the start date. Full refund will be processed within 7-14 business days.", icon: RefreshCw, accent: "blue" as const },
          { title: "Cancellation Terms", content: "Campaigns cannot be cancelled after display has begun. In case of technical issues, clients may be eligible for a partial refund based on the duration of the disruption.", icon: Clock, accent: "violet" as const },
          { title: "Refund Process", content: "Refunds are processed back to the original payment method used for the purchase.", icon: CreditCard, accent: "emerald" as const },
          { title: "Exceptional Circumstances", content: "In cases of force majeure or circumstances beyond our control, we reserve the right to modify or suspend the campaign with appropriate compensation.", icon: AlertTriangle, accent: "amber" as const },
        ];

  return (
    <div className="relative min-h-screen">
      <SEO
        title="Return & Cancellation Policy — Fusion Media"
        description="Fair and transparent cancellation and refund terms for Fusion Media advertising campaigns."
      />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHero
          eyebrow="Return & Cancellation"
          title={language === "ar" ? "سياسة" : "Return"}
          accent={language === "ar" ? "الإسترداد" : "policy"}
          subtitle={
            language === "ar"
              ? "نلتزم بتقديم سياسة إلغاء واسترداد عادلة وشفافة لضمان رضاكم الكامل"
              : "We are committed to providing a fair and transparent cancellation and refund policy for your complete satisfaction."
          }
          centered
        />

        <section ref={ref} className="reveal-on-scroll pb-16 md:pb-24">
          <div className="max-w-4xl mx-auto grid gap-5">
            {sections.map((s, i) => (
              <GlowCard key={i} icon={s.icon} accent={s.accent} title={s.title} description={s.content} />
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-10">
            {language === "ar" ? "آخر تحديث: يناير ٢٠٢٦" : "Last updated: January 2026"}
          </p>
        </section>
      </div>
    </div>
  );
};

export default ReturnPolicy;
